'use client';

import mermaid from 'mermaid';
import { useTheme } from 'next-themes';
import { useEffect, useRef } from 'react';

interface MermaidChartProps {
  chart: string;
  title?: string;
  heading?: string; // for backwards compatibility
  subtitle?: string;
  plotColor?: 'green' | 'orange' | 'blue' | 'yellow' | 'pink'; // only works for xy chart
}

function MermaidChart(props: MermaidChartProps) {
  const { chart, subtitle, title, plotColor = 'orange', heading } = props;
  const mermaidRef = useRef<HTMLDivElement>(null);

  const { resolvedTheme } = useTheme();

  const cssVariables = {
    '--node-bg-color': resolvedTheme === 'dark' ? '#121212' : '#ffffff',
    '--surface-color': resolvedTheme === 'dark' ? '#090909' : '#f6f6f9',
    '--plot-color-orange': resolvedTheme === 'dark' ? '#FF7004' : '#FF7004',
    '--plot-color-green': resolvedTheme === 'dark' ? '#24D583' : '#18a859',
    '--plot-color-yellow': resolvedTheme === 'dark' ? '#D9F22A' : '#D9F22A',
    '--plot-color-blue': resolvedTheme === 'dark' ? '#215389' : '#236fc1',
    '--plot-color-pink': resolvedTheme === 'dark' ? '#ff508a' : '#ff508a',
    '--note-bg-color': resolvedTheme === 'dark' ? '#701337' : '#f7b5ce',
    '--note-stroke-color': resolvedTheme === 'dark' ? '#ff508a' : '#ff508a',
    '--note-text-color': resolvedTheme === 'dark' ? '#f7b5ce' : '#701337',
    '--sequence-activation-fill-0': resolvedTheme === 'dark' ? '#701337' : '#f7b5ce',
    '--sequence-activation-border-0': resolvedTheme === 'dark' ? '#ff508a' : '#ff508a',
    '--sequence-activation-fill-1': resolvedTheme === 'dark' ? '#A1AA24' : '#F5F9C0',
    '--sequence-activation-border-1': resolvedTheme === 'dark' ? '#D9F22A' : '#D9F22A',
    '--sankey-blend-mode': resolvedTheme === 'dark' ? 'normal' : 'multiply',
    '--node-text-color': resolvedTheme === 'dark' ? '#cccccc' : '#1a1a1a',
    '--subtle-text-color': resolvedTheme === 'dark' ? '#888888' : '#4a4a4a',
    '--flowchart-line-color': resolvedTheme === 'dark' ? '#888888' : '#4a4a4a',
    '--flowchart-subgraph-bg': resolvedTheme === 'dark' ? '#543F8E28' : '#7B52D715',
    '--flowchart-subgraph-border': resolvedTheme === 'dark' ? '#543F8E78' : '#7B52D735',
    '--subtler-text-color': resolvedTheme === 'dark' ? '#666666' : '#a1a1a1',
    '--node-quad2-color': resolvedTheme === 'dark' ? '#161616' : '#fafafa',
    '--node-quad3-color': resolvedTheme === 'dark' ? '#191919' : '#f3f3f3',
    '--node-quad4-color': resolvedTheme === 'dark' ? '#161616' : '#fafafa',
    '--node-border-color': resolvedTheme === 'dark' ? '#2f2f2f' : '#d9d9d9',
    '--gantt-grid-color': resolvedTheme === 'dark' ? '#1f1f1f' : '#dddddd',
    '--gantt-done-border': resolvedTheme === 'dark' ? '#543F8E' : '#7B52D7',
    '--gantt-done-fill': resolvedTheme === 'dark' ? '#120C28' : '#EFEBFF',
    '--gantt-active-border': resolvedTheme === 'dark' ? '#24D583' : '#18a859',
    '--gantt-active-bg': resolvedTheme === 'dark' ? '#0a4222' : '#24D583',
    '--gantt-active-text': resolvedTheme === 'dark' ? '#fafafa' : '#4a4a4a',
    '--gantt-crit-border': resolvedTheme === 'dark' ? '#ce2e2e' : '#ce2e2e',
    '--gantt-crit-bg': resolvedTheme === 'dark' ? '#350909' : '#f95a5a',
    '--gantt-today-line': resolvedTheme === 'dark' ? '#215389' : '#236fc1',
    '--gantt-milestone-bg': resolvedTheme === 'dark' ? '#2b2917' : '#f8ea21',
    '--gantt-milestone-border': resolvedTheme === 'dark' ? '#f8ea21' : '#b5a510',
    '--mindmap-branch-0': resolvedTheme === 'dark' ? '#18a859' : '#24D583',
    '--mindmap-node-line-0': resolvedTheme === 'dark' ? '#24D583' : '#18a859',
    '--mindmap-branch-1': resolvedTheme === 'dark' ? '#215389' : '#236fc1',
    '--mindmap-node-line-1': resolvedTheme === 'dark' ? '#215389' : '#236fc1',
    '--mindmap-branch-2': resolvedTheme === 'dark' ? '#D9F22A' : '#D9F22A',
    '--mindmap-node-line-2': resolvedTheme === 'dark' ? '#D9F22A' : '#C8E019',
    '--mindmap-branch-3': resolvedTheme === 'dark' ? '#543F8E' : '#7B52D7',
    '--mindmap-node-line-3': resolvedTheme === 'dark' ? '#543F8E' : '#7B52D7',
  } as const;

  const barPlotColor = `--plot-color-${plotColor}`;

  useEffect(() => {
    mermaid.initialize({
      startOnLoad: true,
      securityLevel: 'loose',
      theme: 'base',
      flowchart: {
        curve: 'basis',
        diagramPadding: 20,
        subGraphTitleMargin: {
          top: 8,
          bottom: 10,
        },
      },
      quadrantChart: {
        quadrantTextTopPadding: 20,
        quadrantLabelFontSize: 14,
        pointTextPadding: 10,
        chartWidth: 800,
        chartHeight: 800,
      },
      gantt: {
        weekday: 'monday',
        fontSize: 10,
      },

      pie: {
        textPosition: 0.7,
      },
      mindmap: {},
      timeline: {},
      sankey: {
        useMaxWidth: true,
        showValues: true,
      },
      // Note that the themeCSS config is very fiddly - read more --> https://stackoverflow.com/questions/67431973/changing-color-of-font-in-mermaid-js-for-a-single-message
      themeCSS: `
      .node rect, .node circle, .node polygon, .node path, .node default, .timeline-node path {
        fill: var(--node-bg-color);
        stroke: var(--node-border-color);
        stroke-width: 1.5px;
      };
      .loopLine {
        stroke: var(--subtler-text-color);
      }
      .labelBox {
        fill: var(--node-bg-color);
                stroke: var(--node-border-color);
                stroke-width: 1.5px;
                color: var(--node-text-color);
      }
      .background {
        fill: none;
      }
      .note {
        fill: var(--note-bg-color);
        stroke: var(--note-stroke-color);
      }
      .noteText tspan {
        fill: var(--note-text-color) !important;
      }
      .divider {
        stroke: var(--node-border-color) !important;
      }
      .bar-plot-0 rect {
        fill: var(${barPlotColor});
      }
      .line-plot-1 path, .axis-line path, .axisl-line path {
        stroke: var(--flowchart-line-color);
      }
      .timeline-node text {
        fill: var(--node-text-color);
        font-size: 18px !important;
        font-weight: 600;
      }
      .label text {
        fill: var(--subtle-text-color);
      }
      .composite.label-container {
        fill: var(--flowchart-subgraph-bg);
        stroke: var(--flowchart-subgraph-border);
      }
      .nodeLabel {
        color: var(--node-text-color);
      }
      .actor tspan {
        fill: var(--node-text-color) !important;
      }
      .messageText {
              fill: var(--subtle-text-color) !important;
            }
      .node-labels text, .entityLabel {
        fill: var(--node-text-color);
      }
      .200 {
        stroke: var(--subtle-text-color) !important;
      }
      .edgePaths path {
        stroke: var(--flowchart-line-color);
      }

      .lineWrapper line {
        stroke: var(--flowchart-line-color);
      }
      .edgeLabel {
        background-color: var(--surface-color);
        padding-block: 4px;
        padding-inline: 6px;
        color: var(--subtle-text-color);
      }
      .classTitle span {
        font-family: var(--font-mono);
      }
      .marker.flowchart, .classDiagram {
        fill: var(--flowchart-line-color);
      }
      .cluster rect {
        fill: var(--flowchart-subgraph-bg);
        padding: 4px;
        stroke: var(--flowchart-subgraph-border);
      }
      .activation0 {
              fill: var(--sequence-activation-fill-0);
              stroke: var(--sequence-activation-border-0);
            }
      .activation1 {
                    fill: var(--sequence-activation-fill-1);
                    stroke: var(--sequence-activation-border-1);
                  }
      .cluster-label span {
        color: var(--subtle-text-color)
      }
      g rect.actor, rect.er.entityBox { stroke:var(--node-border-color);fill: var(--node-bg-color);  };
      .border line {
        stroke: var(--node-border-color) !important;
      };
      .quadrants > .quadrant:nth-of-type(1) rect {
        fill: var(--node-bg-color);
      }
      .quadrants > .quadrant:nth-of-type(1) text {
        fill: var(--node-text-color);
      }
      .quadrants > .quadrant:nth-of-type(2) rect {
              fill: var(--node-quad2-color);
      }
      .quadrants > .quadrant:nth-of-type(2) text {
        fill: var(--node-text-color);
      }
       .quadrants > .quadrant:nth-of-type(3) rect {
                    fill: var(--node-quad3-color);
        }
        .quadrants > .quadrant:nth-of-type(3) text {
          fill: var(--node-text-color);
        }
      .quadrants > .quadrant:nth-of-type(4) rect {
                   fill: var(--node-quad4-color);
       }
       .quadrants > .quadrant:nth-of-type(4) text {
         fill: var(--node-text-color);
       }
       .taskText {
         fill: var(--node-text-color);
         text-anchor: middle;
         font-family: var(--font-sans);
       }
       .taskTextOutsideRight {
         fill: var(--subtle-text-color); ;
         text-anchor: start;
          font-family: var(--font-sans);
       }
       .taskTextOutsideLeft {
         fill: var(--subtle-text-color); ;
         text-anchor: end;
        font-family: var(--font-sans);
       };
       .grid .tick {
         opacity: 1;
         shape-rendering: crispEdges;
         stroke-width: 1;
       }
       .grid .tick line {
        stroke: var(--gantt-grid-color);
       }
       .grid path {
         stroke-width: 0;
       }
       g text.sectionTitle {
        fill: var(--subtle-text-color);
        font-family: var(--font-sans);
        font-weight: 600;
       };
       g rect.task {
          fill: var(--node-bg-color);
          stroke: var(--node-border-color);
          stroke-width: 1px;
       };
       .link {
        mix-blend-mode: var(--sankey-blend-mode) !important;
       };
       g rect.task.milestone {
                 fill: var(--gantt-milestone-bg);
                 stroke: var(--gantt-milestone-border);
¨              };
       g rect.section {
        fill: none;
       }
       rect.task.done0, rect.task.done1, rect.task.done2, rect.task.done3, rect.task.doneCrit1, rect.task.doneCrit0, rect.task.doneCrit2, rect.task.doneCrit3 {
        fill: var(--gantt-done-fill);
        stroke: var(--gantt-done-border);
        };
        .doneText0, .doneText1, .doneText2, .doneText3, .doneCritText0, .doneCritText1, .doneCritText2, .doneCritText3 {
                 fill: var(--subtler-text-color) !important;
                 font-style: italic;
               }
        rect.task.active0, rect.task.active1, rect.task.active2, rect.task.active3 {
                         stroke: var(--gantt-active-border);
                         fill: var(--gantt-active-bg);
        };
        .activeText0, .activeText1, .activeText2, .activeText3, .activeCritText0, .activeCritText1, .activeCritText2, .activeCritText3 {
                         fill: var(--gantt-active-text) !important;
                       }
         rect.task.activeCrit0, rect.task.activeCrit1, rect.task.activeCrit2, rect.task.activeCrit3 {
                                 stroke: var(--gantt-active-border);
                                 fill: var(--gantt-active-bg);
                };
       .crit0, .crit1, .crit2, .crit3 {
           fill: var(--gantt-crit-bg) !important;
           stroke: var(--gantt-crit-border) !important;
       }
        .critText0, .critText1, .critText2, .critText3 {
            font-weight: 600;
        }
        .today {
          stroke-width:2px;
          stroke:var(--gantt-today-line);
          opacity: 0.9
        }
        .legend text {
          font-weight: 600;
          font-size: 12px !important;
        }
        .legend rect {
          stroke: var(--node-border-color) !important;
                  stroke-width: 1px !important;
        }
        .pieOuterCircle, .pieCircle {
          stroke: var(--node-border-color);
        }
        .slice {
          font-weight: 600;
          fill: var(--node-text-color);
        }
        .data-point text {
                  fill: var(--subtle-text-color);
                }
              .data-point circle {
                                fill: var(--subtle-text-color);
                              }
        .section-root circle {
          fill: var(--node-bg-color);
          stroke: var(--node-border-color);
          stroke-width: 1.5px;
        }
        .mindmap-node path, .mindmap-node rect, .mindmap-node circle, .mindmap-node polygon {
          fill: var(--node-bg-color);
          stroke: var(--node-border-color);
          stroke-width: 1.5px;
        }
        .text-inner-tspan {
          fill: var(--node-text-color);
        }
        .edge-depth-0 {
          stroke-width: 10px;
        }
        .edge-depth-1 {
                  stroke-width: 7px;
                }
        .edge-depth-2 {
                  stroke-width: 4px;
                }
        .edge-depth-3 {
                  stroke-width: 3px;
                }
        .section-edge-0 {
          stroke: var(--mindmap-branch-0);
        }
        .section-0 line {
                    stroke: var(--mindmap-node-line-0);
        }
        .section-edge-1 {
                  stroke: var(--mindmap-branch-1);
                }
                .section-1 line {
                            stroke: var(--mindmap-node-line-1);
                }
        .section-edge-2 {
                          stroke: var(--mindmap-branch-2);
                        }
                        .section-2 line {
                                    stroke: var(--mindmap-node-line-2);
                        }
        .section-edge-3 {
                                  stroke: var(--mindmap-branch-3);
                                }
                                .section-3 line {
                                            stroke: var(--mindmap-node-line-3);
                                }
      `,
      fontFamily: 'var(--font-sans)',
    });

    if (mermaidRef.current) {
      try {
        mermaid.run({ nodes: [mermaidRef.current] });
      } catch (error) {
        console.error('Failed to render the Mermaid chart: ', error);
      }
    }
  }, [barPlotColor]);

  return (
    <div
      className="my-6"
      style={{
        borderRadius: 8,
        padding: 6,
        backgroundColor: resolvedTheme === 'dark' ? '#090909' : '#f6f6f9',
        backgroundImage:
          resolvedTheme === 'dark'
            ? 'radial-gradient(circle at 1px 1px, #272727 1px, transparent 0)'
            : 'radial-gradient(circle at 1px 1px, #d6d6d9 1px, transparent 0)',
        backgroundSize: '14px 14px',
        backgroundRepeat: 'repeat',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: resolvedTheme === 'dark' ? '#191919' : '#d9d9d9',
      }}
    >
      {(title || heading) && (
        <div
          style={{
            padding: 3,
            display: 'inline-flex',
            margin: 8,
            fontSize: 'medium',
            fontWeight: 500,
            opacity: 0.85,
            backgroundColor: resolvedTheme === 'dark' ? '#050505' : '#f6f6f977',
          }}
          className="dark:text-neutral-200" // needed for dark because prose not active
        >
          {title ?? heading}
        </div>
      )}
      <div
        style={{
          paddingInline: chart.includes('quadrant') || chart.includes('xychart-beta') ? 40 : 0,
        }}
      >
        <div style={cssVariables} ref={mermaidRef} className="flex justify-center items-center">
          {chart}
        </div>
      </div>
      {subtitle && (
        <div
          style={{
            padding: 3,
            display: 'inline-flex',
            margin: 8,
            fontSize: 'small',
            fontWeight: 500,
            opacity: 0.5,
            backgroundColor: resolvedTheme === 'dark' ? '#050505' : '#f6f6f9',
          }}
          className="dark:text-neutral-300" // needed for dark because prose not active
        >
          {subtitle}
        </div>
      )}
    </div>
  );
}

export default MermaidChart;
